import { ApiError, activateLocation, deactivateLocation, listLocations } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { LocationResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { DeactivateLocationDialog } from '../components/DeactivateLocationDialog';
import { LocationsTable } from '../components/LocationsTable';

export function LocationsPage() {
  const { t } = useTranslation('locations');
  const [locations, setLocations] = useState<LocationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // locations", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogLocation, setDialogLocation] = useState<LocationResponse | null>(null);
  const [dialogAction, setDialogAction] = useState<'deactivate' | 'activate'>('deactivate');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listLocations();
      setLocations(res.locations);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('feedback.loadListError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  function openDeactivate(location: LocationResponse) {
    setDialogAction('deactivate');
    setDialogLocation(location);
  }

  function openActivate(location: LocationResponse) {
    setDialogAction('activate');
    setDialogLocation(location);
  }

  async function handleConfirmed(location: LocationResponse) {
    try {
      if (dialogAction === 'deactivate') {
        await deactivateLocation(location.id);
        setSuccess(t('feedback.deactivatedSuccess', { name: location.name }));
      } else {
        await activateLocation(location.id);
        setSuccess(t('feedback.activatedSuccess', { name: location.name }));
      }
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.list.title')}
        description={t('page.list.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && locations.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{locations.length}</span>{' '}
              {t('unit', { count: locations.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Link to="/dashboard/locations/new">
            <Button size="sm">
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              {t('page.list.newLocation')}
            </Button>
          </Link>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <LocationsTable
              locations={locations}
              loading={loading}
              onDeactivate={openDeactivate}
              onActivate={openActivate}
            />
          )}
        </CardContent>
      </Card>

      <DeactivateLocationDialog
        location={dialogLocation}
        action={dialogAction}
        onClose={() => setDialogLocation(null)}
        onConfirmed={handleConfirmed}
      />
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('locations');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
